import { useEffect, useRef, useState } from 'react';
import { isValidationError } from '@/services/api';
import { useDealerSecretSettingMutations } from './useDealerSecretSettingMutations';
import { useDealerSecretSettingQuery } from './useDealerSecretSettingQuery';

export interface PanelMessage {
    kind: 'success' | 'error';
    text: string;
}

const SAVE_FAILED = 'Failed to save the key.';
const REMOVE_FAILED = 'Failed to remove the key.';
export const SECRET_REJECTED = 'The key was rejected. Check it and try again.';

export const useDealerSecretSettingPanel = (dealerId: string, settingKey: string) => {
    const { data: status, isLoading, isError } = useDealerSecretSettingQuery(dealerId, settingKey);
    const { put, remove } = useDealerSecretSettingMutations(dealerId, settingKey);
    const [formOpen, setFormOpen] = useState(false);
    const [formVersion, setFormVersion] = useState(0);
    const [saveFailed, setSaveFailed] = useState(false);
    const [fieldError, setFieldError] = useState<string | null>(null);
    const [message, setMessage] = useState<PanelMessage | null>(null);
    const [confirmingRemove, setConfirmingRemove] = useState(false);
    const [restoreTriggerFocus, setRestoreTriggerFocus] = useState(false);
    const triggerRef = useRef<HTMLDivElement>(null);
    const confirmRef = useRef<HTMLDivElement>(null);
    const busy = put.isPending || remove.isPending;

    useEffect(() => {
        if (!restoreTriggerFocus || busy) return;
        triggerRef.current?.querySelector('button')?.focus();
        setRestoreTriggerFocus(false);
    }, [restoreTriggerFocus, busy]);

    useEffect(() => {
        if (confirmingRemove) confirmRef.current?.querySelector('button')?.focus();
    }, [confirmingRemove]);

    const closeForm = () => {
        setFormOpen(false);
        setFieldError(null);
        setSaveFailed(false);
        setFormVersion((v) => v + 1);
        setRestoreTriggerFocus(true);
    };

    const toggleForm = () => (formOpen ? closeForm() : setFormOpen(true));

    const startRemove = () => setConfirmingRemove(true);

    const cancelRemove = () => {
        setConfirmingRemove(false);
        setRestoreTriggerFocus(true);
    };

    const submit = (secret: string) => {
        setMessage(null);
        setFieldError(null);
        setSaveFailed(false);
        put.mutate(secret, {
            onSuccess: () => {
                closeForm();
                setMessage({ kind: 'success', text: 'Key saved.' });
            },
            onError: (err) => {
                setSaveFailed(true);
                setFormVersion((v) => v + 1);
                if (isValidationError(err)) {
                    setFieldError(SECRET_REJECTED);
                    return;
                }
                setMessage({ kind: 'error', text: SAVE_FAILED });
            },
            onSettled: () => put.reset(),
        });
    };

    const confirmRemove = () => {
        setMessage(null);
        remove.mutate(undefined, {
            onSuccess: () => {
                setConfirmingRemove(false);
                setRestoreTriggerFocus(true);
                setMessage({ kind: 'success', text: 'Key removed.' });
            },
            onError: () => setMessage({ kind: 'error', text: REMOVE_FAILED }),
            onSettled: () => remove.reset(),
        });
    };

    return {
        status,
        isLoading,
        isError,
        formOpen,
        formVersion,
        fieldError,
        saveFailed,
        message,
        confirmingRemove,
        busy,
        saving: put.isPending,
        removing: remove.isPending,
        triggerRef,
        confirmRef,
        closeForm,
        toggleForm,
        startRemove,
        cancelRemove,
        submit,
        confirmRemove,
    };
};
